import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, Package, ShoppingCart, Tag, Users, LogOut, 
  ArrowLeft, Plus, Edit2, Trash2, Save, X, Loader2, Check, Upload,
  BarChart3, Mail, Star, Eye, TrendingUp, Calendar, Settings, Image
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { 
  getAdminStats, getProducts, getOrders, getPromoCodes,
  createProduct, updateProduct, deleteProduct,
  createPromoCode, updatePromoCode, deletePromoCode,
  updateOrderStatus, deleteOrder, uploadImage
} from '../services/api';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isAdmin, logoutUser, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [promoCodes, setPromoCodes] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [newsletter, setNewsletter] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && (!isAuthenticated || !isAdmin)) {
      navigate('/prijava');
    }
  }, [authLoading, isAuthenticated, isAdmin, navigate]);

  useEffect(() => {
    if (isAdmin) {
      loadData();
    }
  }, [isAdmin, activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      
      if (activeTab === 'dashboard') {
        const statsData = await getAdminStats();
        setStats(statsData);
      } else if (activeTab === 'products') {
        const productsData = await getProducts(true);
        setProducts(productsData);
      } else if (activeTab === 'orders') {
        const ordersData = await getOrders();
        setOrders(ordersData);
      } else if (activeTab === 'promo') {
        const promoData = await getPromoCodes();
        setPromoCodes(promoData);
      } else if (activeTab === 'analytics') {
        try {
          const res = await fetch(`${API_URL}/api/analytics.php?action=stats`, { headers });
          if (res.ok) {
            const data = await res.json();
            setAnalytics(data);
          } else {
            // Set empty analytics if API not available
            setAnalytics({
              total_visits: 0,
              today_visits: 0,
              week_visits: 0,
              month_visits: 0,
              unique_visitors: 0,
              page_stats: [],
              daily_visits: [],
              recent_visits: []
            });
          }
        } catch (e) {
          setAnalytics({
            total_visits: 0,
            today_visits: 0,
            week_visits: 0,
            month_visits: 0,
            unique_visitors: 0,
            page_stats: [],
            daily_visits: [],
            recent_visits: []
          });
        }
      } else if (activeTab === 'newsletter') {
        try {
          const res = await fetch(`${API_URL}/api/newsletter.php?action=list`, { headers });
          if (res.ok) {
            const data = await res.json();
            setNewsletter(data.subscribers || []);
          } else {
            setNewsletter([]);
          }
        } catch (e) {
          setNewsletter([]);
        }
      } else if (activeTab === 'reviews') {
        try {
          const res = await fetch(`${API_URL}/api/reviews.php?action=admin`, { headers });
          if (res.ok) {
            const data = await res.json();
            setReviews(data.reviews || []);
          } else {
            setReviews([]);
          }
        } catch (e) {
          setReviews([]);
        }
      }
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logoutUser();
    navigate('/');
  };

  if (authLoading || !isAdmin) {
    return <div className="admin-page"><div className="loading-container">Učitavanje...</div></div>;
  }

  return (
    <div className="admin-page">
      <aside className="admin-sidebar">
        <div className="admin-logo">
          <Link to="/">
            <img 
              src="https://customer-assets.emergentagent.com/job_puter-klub/artifacts/wdaauptq_Logo_V%20%281%29.png" 
              alt="Ganetov" 
            />
          </Link>
        </div>
        
        <nav className="admin-nav">
          <button 
            className={activeTab === 'dashboard' ? 'active' : ''} 
            onClick={() => setActiveTab('dashboard')}
          >
            <LayoutDashboard size={18} />
            Dashboard
          </button>
          <button 
            className={activeTab === 'analytics' ? 'active' : ''} 
            onClick={() => setActiveTab('analytics')}
          >
            <BarChart3 size={18} />
            Posete
          </button>
          <button 
            className={activeTab === 'products' ? 'active' : ''} 
            onClick={() => setActiveTab('products')}
          >
            <Package size={18} />
            Proizvodi
          </button>
          <button 
            className={activeTab === 'orders' ? 'active' : ''} 
            onClick={() => setActiveTab('orders')}
          >
            <ShoppingCart size={18} />
            Narudžbine
          </button>
          <button 
            className={activeTab === 'promo' ? 'active' : ''} 
            onClick={() => setActiveTab('promo')}
          >
            <Tag size={18} />
            Promo kodovi
          </button>
          <button 
            className={activeTab === 'newsletter' ? 'active' : ''} 
            onClick={() => setActiveTab('newsletter')}
          >
            <Mail size={18} />
            Newsletter
          </button>
          <button 
            className={activeTab === 'reviews' ? 'active' : ''} 
            onClick={() => setActiveTab('reviews')}
          >
            <Star size={18} />
            Recenzije
          </button>
          <button 
            className={activeTab === 'testimonials' ? 'active' : ''} 
            onClick={() => setActiveTab('testimonials')}
          >
            <Image size={18} />
            Utisci
          </button>
          <button 
            className={activeTab === 'settings' ? 'active' : ''} 
            onClick={() => setActiveTab('settings')}
          >
            <Settings size={18} />
            Podešavanja
          </button>
        </nav>

        <div className="admin-footer">
          <div className="admin-user">
            <Users size={18} />
            <span>{user?.name}</span>
          </div>
          <button className="logout-btn" onClick={handleLogout}>
            <LogOut size={18} />
          </button>
        </div>
      </aside>

      <main className="admin-content">
        {activeTab === 'dashboard' && (
          <DashboardTab stats={stats} loading={loading} />
        )}
        {activeTab === 'analytics' && (
          <AnalyticsTab analytics={analytics} loading={loading} onRefresh={loadData} />
        )}
        {activeTab === 'products' && (
          <ProductsTab products={products} loading={loading} onRefresh={loadData} />
        )}
        {activeTab === 'orders' && (
          <OrdersTab orders={orders} loading={loading} onRefresh={loadData} />
        )}
        {activeTab === 'promo' && (
          <PromoTab promoCodes={promoCodes} loading={loading} onRefresh={loadData} />
        )}
        {activeTab === 'newsletter' && (
          <NewsletterTab subscribers={newsletter} loading={loading} onRefresh={loadData} />
        )}
        {activeTab === 'reviews' && (
          <ReviewsTab reviews={reviews} loading={loading} onRefresh={loadData} />
        )}
        {activeTab === 'testimonials' && (
          <TestimonialsTab loading={loading} onRefresh={loadData} />
        )}
        {activeTab === 'settings' && (
          <SettingsTab loading={loading} />
        )}
      </main>
    </div>
  );
};

// Dashboard Tab
const DashboardTab = ({ stats, loading }) => {
  if (loading || !stats) {
    return <div className="loading-container"><Loader2 className="loading-spinner" size={32} /></div>;
  }

  return (
    <div className="admin-tab">
      <h1>Dashboard</h1>
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon orders"><ShoppingCart size={24} /></div>
          <div className="stat-info">
            <span className="stat-value">{stats.total_orders}</span>
            <span className="stat-label">Ukupno narudžbina</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon pending"><Package size={24} /></div>
          <div className="stat-info">
            <span className="stat-value">{stats.pending_orders}</span>
            <span className="stat-label">Na čekanju</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon products"><Tag size={24} /></div>
          <div className="stat-info">
            <span className="stat-value">{stats.total_products}</span>
            <span className="stat-label">Proizvoda</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon revenue"><LayoutDashboard size={24} /></div>
          <div className="stat-info">
            <span className="stat-value">{stats.total_revenue.toLocaleString('sr-RS')}</span>
            <span className="stat-label">Ukupan prihod (RSD)</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Products Tab
const ProductsTab = ({ products, loading, onRefresh }) => {
  const [editingId, setEditingId] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [formData, setFormData] = useState({
    name: '', size: '', price: '', description: '', image: '', active: true, is_bestseller: false
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleEdit = (product) => {
    setEditingId(product.id);
    setFormData({
      name: product.name,
      size: product.size,
      price: product.price,
      description: product.description || '',
      image: product.image,
      active: product.active,
      is_bestseller: product.is_bestseller || false
    });
  };

  const handleImageUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const result = await uploadImage(file, 'product');
      if (result.url) {
        setFormData(prev => ({ ...prev, image: result.url }));
      }
    } catch (err) {
      console.error('Upload error:', err);
      alert('Greška pri uploadu slike');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async (id = null) => {
    setSaving(true);
    try {
      const data = { ...formData, price: parseInt(formData.price) };
      if (id) {
        await updateProduct(id, data);
      } else {
        await createProduct(data);
      }
      setEditingId(null);
      setShowNew(false);
      setFormData({ name: '', size: '', price: '', description: '', image: '', active: true });
      onRefresh();
    } catch (err) {
      console.error('Error saving product:', err);
      alert('Greška pri čuvanju proizvoda');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Da li ste sigurni da želite da obrišete ovaj proizvod?')) {
      try {
        await deleteProduct(id);
        onRefresh();
      } catch (err) {
        console.error('Error deleting product:', err);
      }
    }
  };

  if (loading) {
    return <div className="loading-container"><Loader2 className="loading-spinner" size={32} /></div>;
  }

  return (
    <div className="admin-tab">
      <div className="tab-header">
        <h1>Proizvodi</h1>
        <button className="add-btn" onClick={() => setShowNew(true)}>
          <Plus size={18} />
          Dodaj proizvod
        </button>
      </div>

      {showNew && (
        <div className="edit-form product-edit-form">
          <h3>Novi proizvod</h3>
          <div className="product-form-layout">
            <div className="product-image-section">
              <div className="product-image-preview">
                {formData.image ? (
                  <img src={formData.image} alt="Preview" />
                ) : (
                  <div className="no-image">Nema slike</div>
                )}
              </div>
              <div className="product-image-controls">
                <input 
                  type="text" 
                  placeholder="URL slike" 
                  value={formData.image} 
                  onChange={(e) => setFormData({ ...formData, image: e.target.value })} 
                />
                <label className="upload-btn">
                  {uploading ? <Loader2 size={14} className="loading-spinner" /> : <Upload size={14} />}
                  {uploading ? 'Upload...' : 'Upload'}
                  <input 
                    type="file" 
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e.target.files[0])}
                    disabled={uploading}
                    style={{ display: 'none' }}
                  />
                </label>
              </div>
            </div>
            <div className="product-details-section">
              <input placeholder="Naziv proizvoda" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
              <input placeholder="Veličina (npr. 350g)" value={formData.size} onChange={(e) => setFormData({ ...formData, size: e.target.value })} />
              <input placeholder="Cena (RSD)" type="number" value={formData.price} onChange={(e) => setFormData({ ...formData, price: e.target.value })} />
              <textarea placeholder="Opis proizvoda" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={3} />
              <label className="checkbox-label">
                <input type="checkbox" checked={formData.active} onChange={(e) => setFormData({ ...formData, active: e.target.checked })} />
                Aktivan (vidljiv na sajtu)
              </label>
              <label className="checkbox-label">
                <input type="checkbox" checked={formData.is_bestseller} onChange={(e) => setFormData({ ...formData, is_bestseller: e.target.checked })} />
                Najprodavaniji (prikaži bedž)
              </label>
            </div>
          </div>
          <div className="form-actions">
            <button className="save-btn" onClick={() => handleSave()} disabled={saving}>
              {saving ? <Loader2 size={16} className="loading-spinner" /> : <Save size={16} />}
              Sačuvaj
            </button>
            <button className="cancel-btn" onClick={() => { setShowNew(false); setFormData({ name: '', size: '', price: '', description: '', image: '', active: true, is_bestseller: false }); }}><X size={16} /> Otkaži</button>
          </div>
        </div>
      )}

      {editingId && (
        <div className="edit-form product-edit-form">
          <h3>Izmeni proizvod</h3>
          <div className="product-form-layout">
            <div className="product-image-section">
              <div className="product-image-preview">
                {formData.image ? (
                  <img src={formData.image} alt="Preview" />
                ) : (
                  <div className="no-image">Nema slike</div>
                )}
              </div>
              <div className="product-image-controls">
                <input 
                  type="text" 
                  placeholder="URL slike" 
                  value={formData.image} 
                  onChange={(e) => setFormData({ ...formData, image: e.target.value })} 
                />
                <label className="upload-btn">
                  {uploading ? <Loader2 size={14} className="loading-spinner" /> : <Upload size={14} />}
                  {uploading ? 'Upload...' : 'Upload'}
                  <input 
                    type="file" 
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e.target.files[0])}
                    disabled={uploading}
                    style={{ display: 'none' }}
                  />
                </label>
              </div>
            </div>
            <div className="product-details-section">
              <input placeholder="Naziv proizvoda" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
              <input placeholder="Veličina (npr. 350g)" value={formData.size} onChange={(e) => setFormData({ ...formData, size: e.target.value })} />
              <input placeholder="Cena (RSD)" type="number" value={formData.price} onChange={(e) => setFormData({ ...formData, price: e.target.value })} />
              <textarea placeholder="Opis proizvoda" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={3} />
              <label className="checkbox-label">
                <input type="checkbox" checked={formData.active} onChange={(e) => setFormData({ ...formData, active: e.target.checked })} />
                Aktivan (vidljiv na sajtu)
              </label>
              <label className="checkbox-label">
                <input type="checkbox" checked={formData.is_bestseller} onChange={(e) => setFormData({ ...formData, is_bestseller: e.target.checked })} />
                Najprodavaniji (prikaži bedž)
              </label>
            </div>
          </div>
          <div className="form-actions">
            <button className="save-btn" onClick={() => handleSave(editingId)} disabled={saving}>
              {saving ? <Loader2 size={16} className="loading-spinner" /> : <Save size={16} />}
              Sačuvaj izmene
            </button>
            <button className="cancel-btn" onClick={() => { setEditingId(null); setFormData({ name: '', size: '', price: '', description: '', image: '', active: true }); }}><X size={16} /> Otkaži</button>
          </div>
        </div>
      )}

      <div className="products-grid-admin">
        {products.map((product) => (
          <div key={product.id} className={`product-card-admin ${!product.active ? 'inactive' : ''}`}>
            <div className="product-card-image">
              <img src={product.image} alt={product.name} />
              {!product.active && <div className="inactive-badge">NEAKTIVAN</div>}
            </div>
            <div className="product-card-info">
              <h4>{product.name}</h4>
              <p className="product-size">{product.size}</p>
              <p className="product-price">{product.price.toLocaleString('sr-RS')} RSD</p>
            </div>
            <div className="product-card-actions">
              <button className="edit-btn" onClick={() => handleEdit(product)}>
                <Edit2 size={16} /> Izmeni
              </button>
              <button className="delete-btn" onClick={() => handleDelete(product.id)}>
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Orders Tab
const OrdersTab = ({ orders, loading, onRefresh }) => {
  const [updatingId, setUpdatingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [filterEmail, setFilterEmail] = useState('');
  const [showCustomerOrders, setShowCustomerOrders] = useState(null);

  const handleStatusChange = async (orderId, newStatus) => {
    setUpdatingId(orderId);
    try {
      await updateOrderStatus(orderId, newStatus);
      onRefresh();
    } catch (err) {
      console.error('Error updating status:', err);
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDelete = async (orderId) => {
    if (window.confirm('Da li ste sigurni da želite da obrišete ovu porudžbinu?')) {
      setDeletingId(orderId);
      try {
        await deleteOrder(orderId);
        onRefresh();
      } catch (err) {
        console.error('Error deleting order:', err);
        alert('Greška pri brisanju');
      } finally {
        setDeletingId(null);
      }
    }
  };

  const getStatusLabel = (status) => {
    const labels = { pending: 'Na čekanju', confirmed: 'Potvrđeno', shipped: 'Poslato', delivered: 'Dostavljeno', cancelled: 'Otkazano' };
    return labels[status] || status;
  };

  // Grupisanje po email-u
  const customerStats = orders.reduce((acc, order) => {
    const email = order.customer.email;
    if (!acc[email]) {
      acc[email] = { name: order.customer.name, email, count: 0, total: 0, orders: [] };
    }
    acc[email].count++;
    acc[email].total += order.total;
    acc[email].orders.push(order);
    return acc;
  }, {});

  const filteredOrders = filterEmail 
    ? orders.filter(o => o.customer.email.toLowerCase().includes(filterEmail.toLowerCase()))
    : orders;

  if (loading) {
    return <div className="loading-container"><Loader2 className="loading-spinner" size={32} /></div>;
  }

  return (
    <div className="admin-tab">
      <h1>Narudžbine ({orders.length})</h1>
      
      {/* Statistika kupaca */}
      <div className="customer-stats">
        <h3>Kupci ({Object.keys(customerStats).length})</h3>
        <div className="customer-list">
          {Object.values(customerStats)
            .sort((a, b) => b.count - a.count)
            .slice(0, 10)
            .map(c => (
              <div 
                key={c.email} 
                className={`customer-stat-item ${showCustomerOrders === c.email ? 'active' : ''}`}
                onClick={() => setShowCustomerOrders(showCustomerOrders === c.email ? null : c.email)}
              >
                <div className="customer-info">
                  <strong>{c.name}</strong>
                  <span>{c.email}</span>
                </div>
                <div className="customer-numbers">
                  <span className="order-count">{c.count} narudžb.</span>
                  <span className="order-total">{c.total.toLocaleString('sr-RS')} RSD</span>
                </div>
              </div>
            ))}
        </div>
      </div>

      {/* Filter */}
      <div className="orders-filter">
        <input 
          type="text" 
          placeholder="Pretraži po email-u..." 
          value={filterEmail}
          onChange={(e) => setFilterEmail(e.target.value)}
        />
        {filterEmail && <button onClick={() => setFilterEmail('')}><X size={16} /></button>}
      </div>
      
      <div className="orders-grid">
        {(showCustomerOrders ? customerStats[showCustomerOrders]?.orders : filteredOrders).map((order) => (
          <div key={order.id} className="order-card-admin">
            <div className="order-card-header">
              <span className="order-id">#{order.id.slice(0, 8)}</span>
              <span className="order-date">{new Date(order.created_at).toLocaleDateString('sr-RS')}</span>
              <button 
                className="delete-order-btn" 
                onClick={() => handleDelete(order.id)}
                disabled={deletingId === order.id}
              >
                {deletingId === order.id ? <Loader2 size={14} className="loading-spinner" /> : <Trash2 size={14} />}
              </button>
            </div>
            
            <div className="order-customer">
              <strong>{order.customer.name}</strong>
              <p>{order.customer.email}</p>
              <p>{order.customer.phone}</p>
              <p>{order.customer.address}, {order.customer.city} {order.customer.postal_code}</p>
            </div>

            <div className="order-items-list">
              {order.items.map((item, idx) => (
                <div key={idx} className="order-item-row">
                  <span className="item-qty">{item.quantity} x {item.product_size || item.size || ''}</span>
                  <span className="item-price">x{item.quantity} = {(item.price * item.quantity).toLocaleString('sr-RS')} RSD</span>
                </div>
              ))}
            </div>

            <div className="order-totals">
              {order.discount > 0 && (
                <div className="order-discount">
                  Popust: -{order.discount.toLocaleString('sr-RS')} RSD
                  {order.promo_code && <span className="promo-code-used"> (kod: {order.promo_code})</span>}
                </div>
              )}
              <div className="order-total">Ukupno: <strong>{order.total.toLocaleString('sr-RS')} RSD</strong></div>
            </div>

            <div className="order-status-select">
              <label>Status:</label>
              <select 
                value={order.status} 
                onChange={(e) => handleStatusChange(order.id, e.target.value)}
                disabled={updatingId === order.id}
              >
                <option value="pending">Na čekanju</option>
                <option value="confirmed">Potvrđeno</option>
                <option value="shipped">Poslato</option>
                <option value="delivered">Dostavljeno</option>
                <option value="cancelled">Otkazano</option>
              </select>
              {updatingId === order.id && <Loader2 size={16} className="loading-spinner" />}
            </div>
          </div>
        ))}
      </div>

      {showCustomerOrders && (
        <button className="clear-filter-btn" onClick={() => setShowCustomerOrders(null)}>
          Prikaži sve narudžbine
        </button>
      )}
    </div>
  );
};

// Promo Tab
const PromoTab = ({ promoCodes, loading, onRefresh }) => {
  const [showNew, setShowNew] = useState(false);
  const [formData, setFormData] = useState({ code: '', discount: '', active: true, usage_limit: '' });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await createPromoCode({
        ...formData,
        discount: parseInt(formData.discount),
        usage_limit: formData.usage_limit ? parseInt(formData.usage_limit) : null
      });
      setShowNew(false);
      setFormData({ code: '', discount: '', active: true, usage_limit: '' });
      onRefresh();
    } catch (err) {
      console.error('Error creating promo:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Da li ste sigurni?')) {
      try {
        await deletePromoCode(id);
        onRefresh();
      } catch (err) {
        console.error('Error deleting promo:', err);
      }
    }
  };

  if (loading) {
    return <div className="loading-container"><Loader2 className="loading-spinner" size={32} /></div>;
  }

  return (
    <div className="admin-tab">
      <div className="tab-header">
        <h1>Promo kodovi</h1>
        <button className="add-btn" onClick={() => setShowNew(true)}>
          <Plus size={18} />
          Novi kod
        </button>
      </div>

      {showNew && (
        <div className="edit-form">
          <h3>Novi promo kod</h3>
          <div className="form-grid">
            <input placeholder="Kod (npr. POPUST20)" value={formData.code} onChange={(e) => setFormData({ ...formData, code: e.target.value })} />
            <input placeholder="Popust (%)" type="number" value={formData.discount} onChange={(e) => setFormData({ ...formData, discount: e.target.value })} />
            <input placeholder="Limit korišćenja (opciono)" type="number" value={formData.usage_limit} onChange={(e) => setFormData({ ...formData, usage_limit: e.target.value })} />
            <label className="checkbox-label">
              <input type="checkbox" checked={formData.active} onChange={(e) => setFormData({ ...formData, active: e.target.checked })} />
              Aktivan
            </label>
          </div>
          <div className="form-actions">
            <button className="save-btn" onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 size={16} className="loading-spinner" /> : <Save size={16} />}
              Sačuvaj
            </button>
            <button className="cancel-btn" onClick={() => setShowNew(false)}><X size={16} /></button>
          </div>
        </div>
      )}

      <div className="data-table">
        <table>
          <thead>
            <tr>
              <th>Kod</th>
              <th>Popust</th>
              <th>Korišćeno</th>
              <th>Limit</th>
              <th>Status</th>
              <th>Akcije</th>
            </tr>
          </thead>
          <tbody>
            {promoCodes.map((promo) => (
              <tr key={promo.id}>
                <td><strong>{promo.code}</strong></td>
                <td>{promo.discount}%</td>
                <td>{promo.used_count}</td>
                <td>{promo.usage_limit || '∞'}</td>
                <td><span className={`status-badge ${promo.active ? 'active' : 'inactive'}`}>{promo.active ? 'Aktivan' : 'Neaktivan'}</span></td>
                <td>
                  <button className="icon-btn delete" onClick={() => handleDelete(promo.id)}><Trash2 size={16} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Images Tab - Za upravljanje slikama na sajtu
const ImagesTab = ({ settings, loading, onRefresh }) => {
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState({});
  const [message, setMessage] = useState('');

  useEffect(() => {
    setFormData(settings);
  }, [settings]);

  const imageFields = [
    { key: 'hero_image', label: 'Hero slika (glavna)', description: 'Velika slika na vrhu sajta' },
    { key: 'brand_story_image', label: 'Naša priča - slika', description: 'Slika u sekciji "O nama"' },
    { key: 'process_image', label: 'Proces - slika', description: 'Slika procesa proizvodnje' },
    { key: 'highlight_image', label: 'Highlight slika', description: 'Istaknuta slika proizvoda' },
    { key: 'standards_image', label: 'Standardi - slika', description: 'Slika u sekciji "Naši standardi"' },
    { key: 'instagram_image_1', label: 'Instagram slika 1', description: 'Prva Instagram slika' },
    { key: 'instagram_image_2', label: 'Instagram slika 2', description: 'Druga Instagram slika' },
    { key: 'instagram_image_3', label: 'Instagram slika 3', description: 'Treća Instagram slika' },
    { key: 'instagram_image_4', label: 'Instagram slika 4', description: 'Četvrta Instagram slika' },
  ];

  const handleFileUpload = async (key, file) => {
    if (!file) return;
    
    setUploading(prev => ({ ...prev, [key]: true }));
    setMessage('');
    
    try {
      const result = await uploadImage(file, key);
      if (result.url) {
        setFormData(prev => ({ ...prev, [key]: result.url }));
        setMessage(`Slika "${key}" uspešno uploadovana!`);
      }
    } catch (err) {
      console.error('Upload error:', err);
      setMessage('Greška pri uploadu: ' + (err.response?.data?.error || err.message));
    } finally {
      setUploading(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    try {
      await updateSiteSettings(formData);
      setMessage('Podešavanja su sačuvana! Osvežite stranicu (Ctrl+Shift+R) da vidite promene.');
      onRefresh();
    } catch (err) {
      console.error('Save error:', err);
      setMessage('Greška pri čuvanju: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="loading-container"><Loader2 className="loading-spinner" size={32} /></div>;
  }

  return (
    <div className="admin-tab">
      <div className="tab-header">
        <h1>Slike sajta</h1>
        <button className="save-btn" onClick={handleSave} disabled={saving}>
          {saving ? <Loader2 size={16} className="loading-spinner" /> : <Save size={16} />}
          Sačuvaj sve
        </button>
      </div>

      {message && (
        <div className={`admin-message ${message.includes('Greška') ? 'error' : 'success'}`}>
          {message}
        </div>
      )}

      <div className="images-info-box">
        <p><strong>Kako promeniti slike:</strong></p>
        <ol>
          <li>Uploadujte novu sliku klikom na "Izaberi fajl" ili</li>
          <li>Unesite URL slike direktno u polje</li>
          <li>Kliknite "Sačuvaj sve" da sačuvate promene</li>
        </ol>
        <p><em>Napomena: Za proizvode, slike se menjaju u sekciji "Proizvodi".</em></p>
      </div>

      <div className="images-grid">
        {imageFields.map(({ key, label, description }) => (
          <div key={key} className="image-edit-card">
            <div className="image-preview">
              {formData[key] ? (
                <img src={formData[key]} alt={label} />
              ) : (
                <div className="no-image">Nema slike</div>
              )}
            </div>
            <div className="image-info">
              <h4>{label}</h4>
              <p>{description}</p>
              <div className="image-url-input">
                <input 
                  type="text" 
                  placeholder="URL slike"
                  value={formData[key] || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, [key]: e.target.value }))}
                />
              </div>
              <div className="image-upload">
                <label className="upload-btn">
                  {uploading[key] ? (
                    <><Loader2 size={14} className="loading-spinner" /> Uploaduje se...</>
                  ) : (
                    <><Upload size={14} /> Izaberi fajl</>
                  )}
                  <input 
                    type="file" 
                    accept="image/*"
                    onChange={(e) => handleFileUpload(key, e.target.files[0])}
                    disabled={uploading[key]}
                    style={{ display: 'none' }}
                  />
                </label>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="text-settings-section">
        <h2>Tekstovi na sajtu</h2>
        <div className="text-fields-grid">
          <div className="text-field">
            <label>Hero naslov</label>
            <input 
              type="text"
              value={formData.hero_title || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, hero_title: e.target.value }))}
              placeholder="NAŠ NAJBOLJI KIKIRIKI PUTER DO SAD"
            />
          </div>
          <div className="text-field">
            <label>Hero podnaslov</label>
            <textarea 
              value={formData.hero_subtitle || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, hero_subtitle: e.target.value }))}
              placeholder="Jedinstvena receptura..."
              rows={3}
            />
          </div>
          <div className="text-field">
            <label>Naša priča - naslov</label>
            <input 
              type="text"
              value={formData.brand_story_title || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, brand_story_title: e.target.value }))}
              placeholder="PRIČA O BRENDU"
            />
          </div>
          <div className="text-field">
            <label>Naša priča - tekst</label>
            <textarea 
              value={formData.brand_story_text || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, brand_story_text: e.target.value }))}
              placeholder="Naš premium kikiriki puter..."
              rows={4}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// Analytics Tab
const AnalyticsTab = ({ analytics, loading, onRefresh }) => {
  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      onRefresh();
    }, 30000);
    return () => clearInterval(interval);
  }, [onRefresh]);

  if (loading && !analytics) {
    return <div className="loading-container"><Loader2 className="loading-spinner" size={32} /></div>;
  }

  const data = analytics || {
    total_visits: 0,
    today_visits: 0,
    week_visits: 0,
    unique_visitors: 0,
    page_stats: [],
    recent_visits: []
  };

  return (
    <div className="admin-tab">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1 style={{ margin: 0 }}>Statistika poseta</h1>
        <button 
          onClick={onRefresh} 
          className="save-btn" 
          style={{ padding: '8px 16px' }}
          disabled={loading}
        >
          {loading ? <Loader2 size={16} className="loading-spinner" /> : <TrendingUp size={16} />}
          Osveži
        </button>
      </div>
      
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon orders"><Eye size={24} /></div>
          <div className="stat-info">
            <span className="stat-value">{data.total_visits}</span>
            <span className="stat-label">Ukupno poseta</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon pending"><Calendar size={24} /></div>
          <div className="stat-info">
            <span className="stat-value">{data.today_visits}</span>
            <span className="stat-label">Danas</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon products"><TrendingUp size={24} /></div>
          <div className="stat-info">
            <span className="stat-value">{data.week_visits}</span>
            <span className="stat-label">Ova nedelja</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon revenue"><Users size={24} /></div>
          <div className="stat-info">
            <span className="stat-value">{data.unique_visitors}</span>
            <span className="stat-label">Jedinstvenih posetilaca</span>
          </div>
        </div>
      </div>

      {data.total_visits === 0 && (
        <div className="admin-section" style={{ textAlign: 'center', padding: '40px', background: 'rgba(212, 168, 83, 0.1)', borderRadius: '8px' }}>
          <p style={{ color: '#d4a853', marginBottom: '10px' }}>Još nema zabeleženih poseta.</p>
          <p style={{ color: '#888', fontSize: '14px' }}>Posete će se automatski beležiti kada korisnici posete sajt.</p>
        </div>
      )}

      {data.page_stats?.length > 0 && (
        <div className="admin-section">
          <h3>Posete po stranicama</h3>
          <div className="data-table">
            <table>
              <thead>
                <tr>
                  <th>Stranica</th>
                  <th>Broj poseta</th>
                </tr>
              </thead>
              <tbody>
                {data.page_stats.map((page, index) => (
                <tr key={index}>
                  <td>{page.page}</td>
                  <td>{page.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {data.recent_visits?.length > 0 && (
      <div className="admin-section">
        <h3>Poslednje posete</h3>
        <div className="data-table">
          <table>
            <thead>
              <tr>
                <th>Stranica</th>
                <th>Vreme</th>
              </tr>
            </thead>
            <tbody>
              {data.recent_visits.slice(0, 20).map((visit, index) => (
                <tr key={index}>
                  <td>{visit.page}</td>
                  <td>{new Date(visit.created_at).toLocaleString('sr-RS')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      )}
    </div>
  );
};

// Newsletter Tab
const NewsletterTab = ({ subscribers, loading, onRefresh }) => {
  const handleDelete = async (id) => {
    if (!window.confirm('Da li ste sigurni da želite da obrišete ovog pretplatnika?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/newsletter.php?id=${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      onRefresh();
    } catch (error) {
      console.error('Error deleting subscriber:', error);
    }
  };

  if (loading) {
    return <div className="loading-container"><Loader2 className="loading-spinner" size={32} /></div>;
  }

  return (
    <div className="admin-tab">
      <h1>Newsletter pretplatnici ({subscribers.length})</h1>
      
      <div className="data-table">
        <table>
          <thead>
            <tr>
              <th>Email</th>
              <th>Datum prijave</th>
              <th>Akcije</th>
            </tr>
          </thead>
          <tbody>
            {subscribers.map((sub) => (
              <tr key={sub.id}>
                <td>{sub.email}</td>
                <td>{new Date(sub.created_at).toLocaleString('sr-RS')}</td>
                <td>
                  <button className="action-btn delete" onClick={() => handleDelete(sub.id)}>
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
            {subscribers.length === 0 && (
              <tr>
                <td colSpan="3" style={{ textAlign: 'center', padding: '40px' }}>
                  Još nema pretplatnika
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Reviews Tab
const ReviewsTab = ({ reviews, loading, onRefresh }) => {
  const handleApprove = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/reviews.php?action=approve`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ id })
      });
      onRefresh();
    } catch (error) {
      console.error('Error approving review:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Da li ste sigurni da želite da obrišete ovu recenziju?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/reviews.php?action=delete`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ id })
      });
      onRefresh();
    } catch (error) {
      console.error('Error deleting review:', error);
    }
  };

  const renderStars = (rating) => {
    return '★'.repeat(rating) + '☆'.repeat(5 - rating);
  };

  if (loading) {
    return <div className="loading-container"><Loader2 className="loading-spinner" size={32} /></div>;
  }

  const pendingReviews = reviews.filter(r => !r.approved);
  const approvedReviews = reviews.filter(r => r.approved);

  return (
    <div className="admin-tab">
      <h1>Recenzije proizvoda</h1>
      
      {pendingReviews.length > 0 && (
        <div className="admin-section">
          <h3>Na čekanju ({pendingReviews.length})</h3>
          <div className="data-table">
            <table>
              <thead>
                <tr>
                  <th>Proizvod</th>
                  <th>Kupac</th>
                  <th>Ocena</th>
                  <th>Komentar</th>
                  <th>Akcije</th>
                </tr>
              </thead>
              <tbody>
                {pendingReviews.map((review) => (
                  <tr key={review.id}>
                    <td>{review.product_name || review.product_id}</td>
                    <td>{review.customer_name}</td>
                    <td style={{ color: '#d4a853' }}>{renderStars(review.rating)}</td>
                    <td>{review.comment}</td>
                    <td>
                      <button className="action-btn edit" onClick={() => handleApprove(review.id)} title="Odobri">
                        <Check size={16} />
                      </button>
                      <button className="action-btn delete" onClick={() => handleDelete(review.id)} title="Obriši">
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="admin-section">
        <h3>Odobrene recenzije ({approvedReviews.length})</h3>
        <div className="data-table">
          <table>
            <thead>
              <tr>
                <th>Proizvod</th>
                <th>Kupac</th>
                <th>Ocena</th>
                <th>Komentar</th>
                <th>Akcije</th>
              </tr>
            </thead>
            <tbody>
              {approvedReviews.map((review) => (
                <tr key={review.id}>
                  <td>{review.product_name || review.product_id}</td>
                  <td>{review.customer_name}</td>
                  <td style={{ color: '#d4a853' }}>{renderStars(review.rating)}</td>
                  <td>{review.comment}</td>
                  <td>
                    <button className="action-btn delete" onClick={() => handleDelete(review.id)}>
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
              {approvedReviews.length === 0 && (
                <tr>
                  <td colSpan="5" style={{ textAlign: 'center', padding: '40px' }}>
                    Još nema odobrenih recenzija
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Settings Tab
const SettingsTab = ({ loading }) => {
  const [settings, setSettings] = useState({
    promo_bar_enabled: '1',
    promo_bar_text: '',
    free_shipping_threshold: '4000',
    google_analytics_id: ''
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await fetch(`${API_URL}/api/site-settings.php`);
        if (res.ok) {
          const data = await res.json();
          setSettings(prev => ({...prev, ...data}));
        }
      } catch (e) {
        console.error('Error fetching settings:', e);
      }
    };
    fetchSettings();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/site-settings.php?action=update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(settings)
      });
      if (res.ok) {
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      }
    } catch (e) {
      console.error('Error saving settings:', e);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="loading-state"><Loader2 className="spin" size={32} /></div>;
  }

  return (
    <div className="admin-tab">
      <div className="tab-header">
        <h1><Settings size={24} /> Podešavanja sajta</h1>
      </div>

      <div className="settings-form">
        <div className="settings-section">
          <h3>Promo Bar (gornja traka)</h3>
          
          <div className="form-group">
            <label className="toggle-label">
              <input 
                type="checkbox" 
                checked={settings.promo_bar_enabled === '1'}
                onChange={(e) => setSettings({...settings, promo_bar_enabled: e.target.checked ? '1' : '0'})}
              />
              <span className="toggle-switch"></span>
              Prikaži promo bar
            </label>
          </div>

          <div className="form-group">
            <label>Tekst promo bara:</label>
            <input 
              type="text"
              value={settings.promo_bar_text || ''}
              onChange={(e) => setSettings({...settings, promo_bar_text: e.target.value})}
              placeholder="POKLON DOBRODOŠLICE -10% | Kod: PREMIUM10"
            />
          </div>
        </div>

        <div className="settings-section">
          <h3>Dostava</h3>
          
          <div className="form-group">
            <label>Besplatna dostava preko (RSD):</label>
            <input 
              type="number"
              value={settings.free_shipping_threshold || ''}
              onChange={(e) => setSettings({...settings, free_shipping_threshold: e.target.value})}
              placeholder="4000"
            />
            <small>Kupci koji potroše više od ovog iznosa dobijaju besplatnu dostavu</small>
          </div>
        </div>

        <div className="settings-section">
          <h3>Google Analytics</h3>
          
          <div className="form-group">
            <label>Measurement ID:</label>
            <input 
              type="text"
              value={settings.google_analytics_id || ''}
              onChange={(e) => setSettings({...settings, google_analytics_id: e.target.value})}
              placeholder="G-XXXXXXXXXX"
            />
            <small>Unesite vaš Google Analytics Measurement ID (npr. G-ABC123XYZ). Možete ga naći u Google Analytics → Admin → Data Streams</small>
          </div>
        </div>

        <button className="btn-primary save-btn" onClick={handleSave} disabled={saving}>
          {saving ? <Loader2 className="spin" size={18} /> : <Save size={18} />}
          {saving ? 'Čuvam...' : saved ? 'Sačuvano!' : 'Sačuvaj podešavanja'}
        </button>
      </div>
    </div>
  );
};

// Testimonials Tab
const TestimonialsTab = ({ loading, onRefresh }) => {
  const [testimonials, setTestimonials] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [newCaption, setNewCaption] = useState('');

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const res = await fetch(`${API_URL}/api/testimonials.php`);
      if (res.ok) {
        const data = await res.json();
        setTestimonials(data);
      }
    } catch (e) {
      console.error('Error fetching testimonials:', e);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      // Upload slike
      const uploadResult = await uploadImage(file);
      
      // Sačuvaj testimonial
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/testimonials.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          image_url: uploadResult.url,
          caption: newCaption
        })
      });

      if (res.ok) {
        setNewCaption('');
        fetchTestimonials();
      }
    } catch (e) {
      console.error('Error uploading testimonial:', e);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Obrisati ovaj utisak?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/testimonials.php?action=delete`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ id })
      });
      if (res.ok) {
        fetchTestimonials();
      }
    } catch (e) {
      console.error('Error deleting testimonial:', e);
    }
  };

  if (loading) {
    return <div className="loading-state"><Loader2 className="spin" size={32} /></div>;
  }

  return (
    <div className="admin-tab">
      <div className="tab-header">
        <h1><Image size={24} /> Utisci kupaca (Screenshots)</h1>
      </div>

      <div className="upload-section">
        <h3>Dodaj novi utisak</h3>
        <div className="form-group">
          <label>Opis (opciono):</label>
          <input 
            type="text"
            value={newCaption}
            onChange={(e) => setNewCaption(e.target.value)}
            placeholder="npr. Zadovoljan kupac iz Beograda"
          />
        </div>
        <div className="upload-area">
          <input 
            type="file" 
            id="testimonial-upload" 
            accept="image/*"
            onChange={handleUpload}
            disabled={uploading}
            style={{ display: 'none' }}
          />
          <label htmlFor="testimonial-upload" className="upload-btn">
            {uploading ? <Loader2 className="spin" size={18} /> : <Upload size={18} />}
            {uploading ? 'Uploadujem...' : 'Izaberi sliku'}
          </label>
        </div>
      </div>

      <div className="testimonials-list">
        <h3>Postojeći utisci ({testimonials.length})</h3>
        <div className="testimonials-grid-admin">
          {testimonials.map((t) => (
            <div key={t.id} className="testimonial-item-admin">
              <img src={t.image_url} alt={t.caption || 'Utisak'} />
              {t.caption && <p>{t.caption}</p>}
              <button className="delete-btn" onClick={() => handleDelete(t.id)}>
                <Trash2 size={16} /> Obriši
              </button>
            </div>
          ))}
          {testimonials.length === 0 && (
            <p className="no-items">Još nema uploadovanih utisaka</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
